import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DateTime } from 'luxon';

import { AddressGuidebookService, ReservationSummary } from 'src/shared/services/address-guidebook.service';
import { DemoDataService } from 'src/shared/services/demo-data.service';
import { ReservationLockCodesService } from 'src/shared/services/reservation-lock-codes.service';
import { manualAccessCode, ReservationAccessCode } from 'src/shared/services/reservation-access-code.service';

import { TranslationPipe } from 'src/shared/pipes/translation.pipe';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';

const MAX_UPCOMING_STAYS = 3;
const STAYS_PER_PAGE = 100;
const MAX_STAY_PAGES = 20;

export interface UpcomingStay {
  reservation: ReservationSummary;
  label: string;
  code: string | null;
}

function stayLabel(reservation: ReservationSummary): string {
  const raw = reservation as any;
  const day = (date?: string) => date
    ? new Date(`${date}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : '';
  const dates = [day(reservation.check_in_date), day(reservation.check_out_date)].filter(Boolean).join(' – ');
  const guests: any[] = raw.guests ?? [];
  const primary = guests.find(item => item?.is_booker) ?? guests.find(item => item?.is_staying) ?? guests[0];
  const guest = raw.source_data?.guest || {};
  const name = (primary?.name || '').trim()
    || (guest.name || '').trim()
    || raw.guest_name
    || [guest.first_name || raw.guest_first_name, guest.last_name || raw.guest_last_name].filter(Boolean).join(' ').trim()
    || new TranslationPipe().transform('Guest');
  return `${dates} · ${name}`;
}

// The code guests will see: the host's own beats the smart lock's.
export function stayCode(reservation: ReservationSummary): string | null {
  const codes = (reservation as { access_codes?: ReservationAccessCode[] }).access_codes ?? [];
  return manualAccessCode({ access_codes: codes })?.code ?? codes[0]?.code ?? null;
}

// The next stays, by check-in: a stay still shows on its check-out day. `today` is the host's local day.
export function pickUpcomingStays(reservations: ReservationSummary[], today: string): UpcomingStay[] {
  return reservations
    .filter(reservation => (reservation.check_out_date || '') >= today)
    .sort((a, b) => (a.check_in_date || '').localeCompare(b.check_in_date || ''))
    .slice(0, MAX_UPCOMING_STAYS)
    .map(reservation => ({ reservation, label: stayLabel(reservation), code: stayCode(reservation) }));
}

/**
 * How to Get In's door code: whether a smart lock makes the codes, and the next
 * stays' codes. Shown on the guidebook card and in its editor; a stay opens the
 * reservation, where the code is added or changed.
 */
@Component({
  selector: 'guidebook-door-codes',
  templateUrl: './guidebook-door-codes.component.html',
  styleUrls: ['./guidebook-door-codes.component.scss'],
  standalone: true,
  imports: [CommonModule, TidyUiComponentsModule, SharedModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GuidebookDoorCodesComponent {
  private readonly addressGuidebookService = inject(AddressGuidebookService);
  private readonly reservationLockCodes = inject(ReservationLockCodesService);
  private readonly isDemo = inject(DemoDataService).isDemoAccount();

  readonly addressId = input.required<number>();
  /** Bump to reload, e.g. after a stay's code was edited. */
  readonly reloadKey = input(0);
  readonly openStay = output<ReservationSummary>();
  /** The lock to make a one-off code on, preselected in Generate Access Code. */
  readonly generateCode = output<number>();

  readonly isLoading = signal(true);
  /** The connected locks' names, joined; empty when no smart lock is connected. */
  readonly lockNames = signal('');
  readonly firstLockId = signal<number | null>(null);
  readonly stays = signal<UpcomingStay[]>([]);

  constructor() {
    effect(() => {
      this.reloadKey();
      void this.load(this.addressId());
    });
  }

  private async load(addressId: number) {
    this.isLoading.set(true);
    const today = DateTime.local().toISODate() ?? '';
    const [locks, reservations] = await Promise.all([
      this.reservationLockCodes.getAssignedLocks(addressId).catch(() => []),
      this.isDemo
        ? Promise.resolve([] as ReservationSummary[])
        : this.loadStaysFrom(addressId, today).catch(() => []),
    ]);
    if (this.addressId() !== addressId) { return; }
    this.lockNames.set(locks.map(lock => lock.display_name || lock.device_name || new TranslationPipe().transform('Smart Lock')).join(', '));
    this.firstLockId.set(locks[0]?.id ?? null);
    this.stays.set(pickUpcomingStays(reservations, today));
    this.isLoading.set(false);
  }

  // Pages come latest check-in first, so the soonest stays are on the last pages: read on until a
  // page ends with a stay that checked out before today.
  private async loadStaysFrom(addressId: number, today: string): Promise<ReservationSummary[]> {
    const stays: ReservationSummary[] = [];
    for (let page = 1; page <= MAX_STAY_PAGES; page++) {
      const { reservations, hasNextPage } = await this.addressGuidebookService.listReservationsPageForAddress(
        addressId, 'active', page, STAYS_PER_PAGE, ['access_codes', 'guests']
      );
      stays.push(...reservations);
      const last = reservations[reservations.length - 1];
      if (!hasNextPage || !last || (last.check_out_date || '') < today) { break; }
    }
    return stays;
  }

  onGenerateCode = () => {
    const lockId = this.firstLockId();
    if (lockId) {
      this.generateCode.emit(lockId);
    }
  };
}
